import { z } from 'zod';

import { isoDatetimeToDate } from '../codecs';
import { processingArgsSchema } from './processing-args';
import { processingArtifactSchema } from './processing-artifact';
import { ProcessingTrigger } from './processing-trigger';
import { ProcessingType } from './processing-type';

export type ProcessingId = Processing['id'];

export type Processing = z.output<typeof processingSchema>;
export type ProcessingDTO = z.input<typeof processingSchema>;

export const processingSchema = z.object({
  args: processingArgsSchema,
  artifacts: z.array(processingArtifactSchema),
  createdAt: isoDatetimeToDate,
  errors: z.array(z.unknown()),
  finishedAt: isoDatetimeToDate.optional(),
  heartbeatTimeout: z.number(),
  id: z.string(),
  isolated: z.boolean(),
  outDir: z.string(),
  pickupTimeout: z.number(),
  pipeline: z.string(),
  progress: z.number(),
  /**
   * The pass a processing belongs to within its session. `1` is the normal forward pass; manually
   * re-triggering an already succeeded processing type starts a new pass, whose root type is kept
   * in `runRootType` and which every processing cascaded from it shares.
   */
  runId: z.number(),
  runRootType: z.enum(ProcessingType).optional(),
  sessionId: z.string(),
  startedAt: isoDatetimeToDate.optional(),
  tenantId: z.string(),
  trigger: z.enum(ProcessingTrigger),
  type: z.enum(ProcessingType),
  updatedAt: isoDatetimeToDate,
  warnings: z.array(z.unknown()),
});

export const ProcessingUtils = {
  hasFailed(processing: Processing): boolean {
    return ProcessingUtils.hasFinished(processing) && processing.errors.length > 0;
  },
  hasFinished(processing: Processing): boolean {
    return typeof processing.finishedAt !== 'undefined';
  },
  hasSucceeded(processing: Processing): boolean {
    return ProcessingUtils.hasFinished(processing) && processing.errors.length === 0;
  },
  isRunning(processing: Processing): boolean {
    return !ProcessingUtils.hasFinished(processing);
  },
};
